import { Controller } from "@hotwired/stimulus"

// Hluti 3 fills itself. Ticking a fee, or typing an hour into „Tímafjöldi",
// puts the amount in the field in the same keystroke - no round trip, so
// nothing on screen blinks and the caret stays where the ref left it. Whether
// it is saved is „Vista"'s business, the same as every other field on the form.
//
// The numbers are not decided here. The gjaldskrá renders its two rates onto
// the row and this only copies one or multiplies the other; a rate that is not
// on the row is a fee the gjaldskrá cannot price, and the field is left blank
// with a word about why rather than filled with a 0.
export default class extends Controller {
  static targets = [ "pickUp", "pickUpAmount", "pickUpRateHint",
                     "car", "carHours", "carAmount", "carHoursHint", "carRateHint" ]
  static values = { pickUp: Number, carHour: Number }

  pickUpChanged() {
    const wanted = this.pickUpTarget.checked

    this.fill(this.pickUpAmountTarget, wanted && this.hasPickUpValue ? this.pickUpValue : null)
    this.show(this.pickUpRateHintTarget, wanted && !this.hasPickUpValue)
  }

  carChanged() {
    const wanted = this.carTarget.checked
    const hours = this.hours()
    const priced = hours > 0 && this.hasCarHourValue

    this.fill(this.carAmountTarget, wanted && priced ? this.drivingFor(hours) : null)
    this.show(this.carHoursHintTarget, wanted && !(hours > 0))
    this.show(this.carRateHintTarget, wanted && hours > 0 && !this.hasCarHourValue)
  }

  // Typing the hours is the claim: a ref who puts 12 in „Tímafjöldi" means to
  // be paid for twelve hours of driving, so the tick follows the number and
  // either order works. Only ever ticks - unticking stays the one deliberate
  // way to say the fee does not apply, hours in the box or not.
  hoursTyped() {
    if (this.hours() > 0) this.carTarget.checked = true

    this.carChanged()
  }

  // To the hundredth, as the column holds them: an hour and a half is 1,5.
  // Read off the mask, which has the number with a point where the screen
  // shows a comma.
  hours() {
    const imask = this.application.getControllerForElementAndIdentifier(this.carHoursTarget, "imask")
    const value = imask && imask.mask ? imask.mask.unmaskedValue : this.carHoursTarget.value.replace(",", ".")

    return Math.round(Number(value) * 100) / 100 || 0
  }

  // Rounded to the króna, half up, as the server rounds it - and worked in
  // hundredths of an hour, so 1,15 hours is not 1.1499999 of them and a
  // half króna does not round the other way here.
  drivingFor(hours) {
    return Math.round(this.carHourValue * Math.round(hours * 100) / 100)
  }

  // The amount fields are masked, so it is the mask that has to be told. Set
  // `value` behind its back and it goes on formatting against a number it does
  // not know about, which shows up on the next keystroke.
  fill(field, amount) {
    const value = amount === null ? "" : String(amount)
    const imask = this.application.getControllerForElementAndIdentifier(field, "imask")

    if (imask && imask.mask) {
      if (imask.mask.unmaskedValue === value) return

      imask.mask.unmaskedValue = value
    } else {
      if (field.value === value) return

      field.value = value
    }

    // Setting a field behind the DOM's back tells nothing that was listening.
    // „Samtals" adds up these very boxes, so a fee that arrived by tick rather
    // than by typing has to say so the same way a keystroke would.
    field.dispatchEvent(new Event("input", { bubbles: true }))
  }

  show(hint, shown) {
    hint.classList.toggle("d-none", !shown)
  }
}
